import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PaymentModal from "../../components/modal/PModal";
import ContractModal from "../../components/modal/ContractModal";
import MakbuzModal from "../../components/modal/MakbuzModal";
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaHome,
  FaIdCard,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaSchool,
  FaUserTie,
  FaFileDownload,
  FaArrowLeft,
  FaMale,
  FaFemale,
  FaUserFriends,
  FaEdit,
  FaTrash,
  FaTimes,
  FaUserPlus,
  FaStickyNote,
  FaPlus,
  FaSave,
} from "react-icons/fa";
import { toast } from "react-hot-toast";
import axios from "axios";
import * as XLSX from "xlsx";

import { formatCurrency, formatDateDisplay } from "../../utils/currency";

const StudentDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { student: initialStudent } = location.state || {};
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [isMakbuzModalOpen, setIsMakbuzModalOpen] = useState(false);
  const [makbuzData, setMakbuzData] = useState(null);
  const [updatedStudent, setUpdatedStudent] = useState(initialStudent);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [studentId, setStudentId] = useState(
    initialStudent?.id || params.id || null
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [userRole] = useState(
    localStorage.getItem("userRole") || "user"
  );
  
  // Sınıf ve şube seçenekleri için state'ler
  const [classOptions, setClassOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);

  // Student Notes için state'ler
  const [studentNotes, setStudentNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [isUpdatingNote, setIsUpdatingNote] = useState(false);

  // Telefon formatlayıcı ve temizleyici yardımcılar (bileşen genelinde)
  const formatPhone = (val) => {
    const digits = String(val || '').replace(/[^0-9]/g, '').slice(0, 10);
    if (!digits) return '';
    let out = '(0';
    if (digits.length <= 3) return out + digits + ')';
    out += digits.slice(0,3) + ') ';
    if (digits.length <= 6) return out + digits.slice(3);
    out += digits.slice(3,6) + ' ';
    if (digits.length <= 8) return out + digits.slice(6);
    out += digits.slice(6,8) + ' ' + digits.slice(8,10);
    return out;
  };
  const extractPhoneNumbers = (val) => {
    let d = String(val || '').replace(/[^0-9]/g, '');
    if (d.startsWith('0')) d = d.slice(1);
    return d.slice(0, 10);
  };

  // İşlem kilidi için useRef kullan - component yeniden render olsa bile değeri korunur
  const processingPayment = useRef(false);
  const lastProcessedPayment = useRef(null);

  // Auth token alma fonksiyonu
  const getAuthToken = () => {
    return localStorage.getItem("authToken") || "";
  };


  // API isteği için gerekli headerları hazırla
  const getRequestHeaders = () => {
    const token = getAuthToken();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  };

  // Sayfa yüklendiğinde veya studentId değiştiğinde öğrenciyi yeniden getir
  useEffect(() => {
    // Sayfa yenilendi/yüklendi, localStorage'dan ID'yi kontrol et
    const storedStudentId = localStorage.getItem("currentStudentId");

    // Önce prop veya param yoluyla gelen ID'yi kontrol et, yoksa localStorage'dan al
    const effectiveId = studentId || storedStudentId;

    if (effectiveId) {
      // Geçerli ID'yi state ve localStorage'a kaydet
      setStudentId(effectiveId);
      localStorage.setItem("currentStudentId", effectiveId);
      // Öğrenciyi getir
      fetchStudent(effectiveId);
      // Öğrenci notlarını getir
      fetchStudentNotes(effectiveId);
    }

    // Sayfa yenileme olayını yakalama
    const handleBeforeUnload = () => {
      // Sayfa yenileme öncesi gerekli işlemler buraya
      console.log("Sayfa yenileniyor...");
    };

    // Sayfa tamamen yüklendiğinde çalışacak
    const handleLoad = () => {
      if (studentId) {
        fetchStudent(studentId);
        fetchStudentNotes(studentId);
      }
    };

    // Event listener'ları ekle
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("load", handleLoad);

    // Component unmount olduğunda event listener'ları temizle
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("load", handleLoad);
    };
  }, [studentId]); // studentId değiştiğinde çalış

  // Sınıf seçeneklerini yükle
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await axios.get(`http://esnakademi.com/api/classes`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        setClassOptions(res.data);
      } catch(e) { 
        console.error("Sınıf seçenekleri yüklenemedi:", e); 
      }
    };
    fetchClasses();
  }, []);

  // Şube seçeneklerini yükle (sınıf seçildiğinde)
  useEffect(() => {
    if (!editFormData.student_class) { 
      setBranchOptions([]); 
      return; 
    }
    
    const fetchBranches = async () => {
      setBranchLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const cls = classOptions.find(c => c.class_level == editFormData.student_class);
        if (!cls) {
          setBranchOptions([]);
          return;
        }
        
        const res = await axios.get(`http://esnakademi.com/api/branches?class_id=${cls.id}`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        setBranchOptions(res.data);
      } catch(e) { 
        console.error("Şube seçenekleri yüklenemedi:", e);
        setBranchOptions([]);
      } finally {
        setBranchLoading(false);
      }
    };
    
    fetchBranches();
  }, [editFormData.student_class, classOptions]);

  // API'den öğrenci verilerini getir
  const fetchStudent = async (id) => {
    setLoading(true);
    try {
      // API URL - sadece liste endpoint'ini kullanıp öğrenci ID'sine göre filtrele

      const timestamp = new Date().getTime();
      const listResponse = await axios.get(
        `http://esnakademi.com/api/students?_t=${timestamp}`,
        {
          headers: getRequestHeaders(),
        }
      );

      // API yanıtı kontrolü
      if (!listResponse.data || !Array.isArray(listResponse.data)) {
        throw new Error("API geçersiz veri döndürdü");
      }

      // ID'ye göre öğrenciyi filtrele
      const filteredStudent = listResponse.data.find(
        (s) => s.id.toString() === id.toString()
      );
      if (filteredStudent) {
        console.log("API'den gelen öğrenci bilgisi:", filteredStudent);

        // Öğrenci verisini state'e kaydet
        setUpdatedStudent(filteredStudent);

        // Debug için logla
        console.log("🔍 CREATED BY DEBUG:", {
          created_by_username: filteredStudent.created_by_username,
          created_by: filteredStudent.created_by,
          has_created_by_username: !!filteredStudent.created_by_username
        });
        console.log("🏫 SCHOOL DEBUG:", {
          school: filteredStudent.school,
          has_school: !!filteredStudent.school
        });
        // Detaylı log görüntüleme
      } else {
        toast.error("Öğrenci bulunamadı");
        // Öğrenci bulunamadıysa, localStorage'daki ID'yi temizle
        localStorage.removeItem("currentStudentId");
      }
    } catch (error) {
      console.error("Öğrenci veri çekme hatası:", error);
      toast.error("Öğrenci bilgileri alınırken bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  // Student Notes API fonksiyonları
  const fetchStudentNotes = async (id) => {
    setNotesLoading(true);
    try {
      const timestamp = new Date().getTime();
      const response = await axios.get(
        `http://esnakademi.com/api/student-notes/${id}?_t=${timestamp}`,
        {
          headers: getRequestHeaders(),
        }
      );

      if (response.data.success) {
        setStudentNotes(response.data.data || []);
      } else {
        console.error("Notlar alınırken hata:", response.data.error);
        setStudentNotes([]);
      }
    } catch (error) {
      console.error("Notlar çekme hatası:", error);
      setStudentNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  const addStudentNote = async () => {
    if (!newNote.trim()) {
      toast.error("Not içeriği boş olamaz");
      return;
    }

    try {
      const response = await axios.post(
        `http://esnakademi.com/api/student-notes`,
        {
          student_id: studentId,
          note_text: newNote.trim(),
        },
        {
          headers: getRequestHeaders(),
        }
      );

      if (response.data.success) {
        toast.success("Not başarıyla eklendi");
        setNewNote("");
        setIsNoteModalOpen(false);
        
        // Notları yeniden yükle (güncel veriyi API'den al)
        if (studentId) {
          await fetchStudentNotes(studentId);
        }
      } else {
        toast.error(response.data.error || "Not eklenirken hata oluştu");
      }
    } catch (error) {
      console.error("Not ekleme hatası:", error);
      toast.error("Not eklenirken bir hata oluştu");
    }
  };

  const updateStudentNote = async (noteId, noteText) => {
    if (isUpdatingNote) return; // Çoklu tıklama engelleme
    
    if (!noteText || noteText.trim() === "") {
      toast.warning("Not içeriği boş olamaz!");
      return;
    }

    setIsUpdatingNote(true);
    try {
      const response = await axios.put(
        `http://esnakademi.com/api/student-notes/${noteId}`,
        {
          note_text: noteText.trim(),
        },
        {
          headers: getRequestHeaders(),
        }
      );

      if (response.data.success) {
        toast.success("Not başarıyla güncellendi");
        // State'i temizle
        setEditingNote(null);
        setEditingNoteText("");
        setNewNote("");
        
        // Notları yeniden yükle (güncel veriyi API'den al)
        if (studentId) {
          await fetchStudentNotes(studentId);
        }
      } else {
        toast.error(response.data.error || "Not güncellenirken hata oluştu");
      }
    } catch (error) {
      console.error("Not güncelleme hatası:", error);
      toast.error("Not güncellenirken bir hata oluştu");
    } finally {
      setIsUpdatingNote(false);
    }
  };

  const deleteStudentNote = async (noteId) => {
    if (!window.confirm("Bu notu silmek istediğinize emin misiniz?")) {
      return;
    }

    try {
      const response = await axios.delete(
        `http://esnakademi.com/api/student-notes/${noteId}`,
        {
          headers: getRequestHeaders(),
        }
      );

      if (response.data.success) {
        toast.success("Not başarıyla silindi");
        
        // Notları yeniden yükle (güncel veriyi API'den al)
        if (studentId) {
          await fetchStudentNotes(studentId);
        }
      } else {
        toast.error(response.data.error || "Not silinirken hata oluştu");
      }
    } catch (error) {
      console.error("Not silme hatası:", error);
      toast.error("Not silinirken bir hata oluştu");
    }
  };

  const openModal = () => {
    // Eğer ödeme işlemi devam ediyorsa engelle
    if (paymentLoading) {
      toast.warning("Ödeme işlemi devam ediyor, lütfen bekleyin...");
      return;
    }

    // Modal açılmadan önce payment_plan verisini düzgün formata getir
    if (updatedStudent && typeof updatedStudent.payment_plan === "string") {
      try {
        // Global obje üzerinden ödeme planını paylaş (modal'da kullanılabilmesi için)
        window.parsedPaymentPlan = parsedPaymentPlan;
      } catch (error) {
        console.error("Modal açılırken ödeme planı hazırlama hatası:", error);
        toast.error("Ödeme planı hazırlanırken hata oluştu!");
      }
    }

    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    processingPayment.current = false;
  };

  // Taksiti ödendi olarak işaretle
  const markInstallmentAsPaid = (installmentId) => {
    if (
      !window.confirm(
        "Bu taksiti ödendi olarak işaretlemek istediğinizden emin misiniz?"
      )
    ) {
      return;
    }

    setPaymentLoading(true);

    // Ödeme planını güncelle
    const updatedPaymentPlan = { ...parsedPaymentPlan };

    // Seçilen taksiti bul
    const selectedBill = updatedPaymentPlan.unpaidBills.find(
      (bill) => bill.id === installmentId
    );

    if (!selectedBill) {
      toast.error("Seçilen taksit bulunamadı!");
      setLoading(false);
      return;
    }

    // Taksiti ödenen taksitlere ekle
    const today = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD formatında

    updatedPaymentPlan.paidInstallments.push({
      ...selectedBill,
      paymentDate: today,
      paymentMethod: "nakit", // Varsayılan ödeme yöntemi
      notes: "Ödendi olarak işaretlendi",
    });

    // Ödenen taksiti listeden çıkar
    updatedPaymentPlan.unpaidBills = updatedPaymentPlan.unpaidBills.filter(
      (bill) => bill.id !== installmentId
    );

    // Mali kayıt oluştur

    // API'ye öğrenci güncelleme isteği gönder
    axios
      .put(
        `http://esnakademi.com/api/students/update?id=${updatedStudent.id}`,
        {
          id: updatedStudent.id,
          name: updatedStudent.name,
          surname: updatedStudent.surname,
          student_number: updatedStudent.student_number,
          student_class: updatedStudent.student_class,
          period: updatedStudent.period || "",
          registration_date: updatedStudent.registration_date || "",
          branch: updatedStudent.branch || "",
          phone: updatedStudent.phone || "",
          email: updatedStudent.email || "",
          photo_url: updatedStudent.photo_url || "",
          gender: updatedStudent.gender || "",
          birth_date: updatedStudent.birth_date || "",
          tc_number: updatedStudent.tc_number || "",
          address: updatedStudent.address || "",
          advisor_teacher: updatedStudent.advisor_teacher || "",
          mother_name: updatedStudent.mother_name || "",
          mother_phone: updatedStudent.mother_phone || "",
          mother_email: updatedStudent.mother_email || "",
          mother_address: updatedStudent.mother_address || "",
          father_name: updatedStudent.father_name || "",
          father_phone: updatedStudent.father_phone || "",
          father_email: updatedStudent.father_email || "",
          father_address: updatedStudent.father_address || "",
          payment_plan: JSON.stringify(updatedPaymentPlan),
        },
        {
          headers: getRequestHeaders(),
        }
      )
      .then((response) => {
        toast.success("Taksit başarıyla ödendi olarak işaretlendi");

        // Öğrenci verisini güncelle
        setUpdatedStudent((prev) => ({
          ...prev,
          payment_plan: JSON.stringify(updatedPaymentPlan),
        }));
      })
      .catch((error) => {
        console.error("Öğrenci güncelleme hatası:", error);
        toast.error("Taksit ödemesi yapılırken bir hata oluştu");
        if (error.response) {
          console.error("API Hata Detayı:", error.response.data);
        }
      })
      .finally(() => {
        setPaymentLoading(false);
      });
  };

  // Düzenleme moduna geç
  const handleEditStudent = () => {
    if (!updatedStudent) {
      toast.error("Öğrenci bilgisi bulunamadı!");
      return;
    }

    // Form verilerini öğrenci bilgileriyle doldur
    const formData = { 
      ...updatedStudent,
      payment_responsible: updatedStudent.payment_responsible || "",
      responsible_tc_number: updatedStudent.responsible_tc_number || "",
      school: updatedStudent.school || ""
    };
    
    setEditFormData(formData);
    // Düzenleme modunu aktifleştir
    setIsEditing(true);
  };

  // Düzenleme formundaki değişiklikleri takip et
  const handleFormChange = (e) => {
    const { name, value } = e.target;

    // İsim alanları için capitalize işlemi (branch hariç - branch seçim dropdown'undan gelir)
    const nameFields = [
      "name",
      "surname",
      "mother_name",
      "father_name",
      "advisor_teacher",
      "address",
      "mother_address",
      "father_address",
    ];
    
    // Telefon alanları
    const phoneFields = ['phone', 'mother_phone', 'father_phone'];
    // Telefon formatlayıcı: 5374989387 -> (0537) 498 93 87
    const formatPhone = (val) => {
      const digits = String(val || '').replace(/[^0-9]/g, '').slice(0, 10);
      if (!digits) return '';
      let out = '(0';
      if (digits.length <= 3) return out + digits + ')';
      out += digits.slice(0,3) + ') ';
      if (digits.length <= 6) return out + digits.slice(3);
      out += digits.slice(3,6) + ' ';
      if (digits.length <= 8) return out + digits.slice(6);
      out += digits.slice(6,8) + ' ' + digits.slice(8,10);
      return out;
    };
    // Kaydetmeden önce sadece rakam bırakmak için
    const extractPhoneNumbers = (val) => {
      let d = String(val || '').replace(/[^0-9]/g, '');
      if (d.startsWith('0')) d = d.slice(1);
      return d.slice(0, 10);
    };
    
    // Email alanları (küçük harf olmalı)
    const emailFields = ['email', 'mother_email', 'father_email'];

    let processedValue = value;

    if (nameFields.includes(name)) {
      // Her kelimenin ilk harfini büyük yap
      processedValue = value
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }
    // Telefon alanları - canlı format: (0XXX) XXX XX XX
    else if (phoneFields.includes(name)) {
      let numbers = String(value || '').replace(/[^0-9]/g, '');
      if (numbers.startsWith('0')) numbers = numbers.slice(1);
      const limited = numbers.slice(0, 10);
      processedValue = formatPhone(limited);
    }
    // Email alanları - küçük harf
    else if (emailFields.includes(name)) {
      processedValue = value.toLowerCase().trim();
    }

    setEditFormData((prev) => {
      const newData = {
        ...prev,
        [name]: processedValue,
      };
      
      // Sınıf değiştiğinde şube seçimini temizle
      if (name === 'student_class') {
        newData.branch = '';
      }
      
      return newData;
    });
  };

  // Düzenlemeleri kaydet
  const handleSaveEdit = async () => {
    if (!editFormData?.id) {
      toast.error("Öğrenci bilgisi bulunamadı!");
      return;
    }

    setLoading(true);
    try {
      // API'ye güncelleme isteği gönder - sadece değişen alanları gönder
      const payload = { ...editFormData };
      // Telefonları temizle (sadece rakamlar - baştaki görsel 0 hariç)
      const cleanDigits = (val) => {
        let d = String(val || '').replace(/[^0-9]/g, '');
        if (d.startsWith('0')) d = d.slice(1);
        return d.slice(0, 10);
      };
      payload.phone = cleanDigits(payload.phone);
      payload.mother_phone = cleanDigits(payload.mother_phone);
      payload.father_phone = cleanDigits(payload.father_phone);
      
      // Branch (şube) alanını büyük harfle gönder (Türkçe karakterler için normalize ve toLocaleUpperCase kullan)
      // Öğrenci kayıt sistemindeki gibi aynı yöntem
      payload.branch = payload.branch ? payload.branch.normalize('NFC').toLocaleUpperCase('tr-TR') : "";
      
      // CRITICAL: payment_plan'ı her zaman updatedStudent'tan al
      payload.payment_plan = updatedStudent.payment_plan;
      
      // Boş alanları null yap
      ['birth_date','registration_date'].forEach((k)=>{
        if (!payload[k] || payload[k]==='' ) payload[k]=null;
      });
      
      await axios.put(
        `http://esnakademi.com/api/students/update?id=${updatedStudent.id}`,
        payload,
        {
          headers: getRequestHeaders(),
        }
      );

      // Öğrenci verisini güncelle - tüm alanları koruyarak
      const finalStudentData = { 
        ...updatedStudent, // Mevcut tüm verileri koru
        ...editFormData,   // Sadece değişen alanları güncelle
        branch: payload.branch, // Branch'i büyük harfle güncelle
        payment_plan: updatedStudent.payment_plan // Güncel payment_plan'ı koru
      };
      
      setUpdatedStudent(finalStudentData);
      toast.success("Öğrenci bilgileri başarıyla güncellendi!");
      // Düzenleme modundan çık
      setIsEditing(false);
    } catch (error) {
      console.error("Öğrenci güncelleme hatası:", error);
      toast.error("Öğrenci bilgileri güncellenirken bir hata oluştu!");
    } finally {
      setLoading(false);
    }
  };

  // Düzenleme modundan çık
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({}); // Form verisini temizle
  };

  if (!updatedStudent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-6 bg-white rounded-lg shadow-lg">
          <FaUser className="mx-auto text-6xl text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold text-gray-700 mb-4">
            Öğrenci bilgisi bulunamadı!
          </h2>
          <button
            onClick={() => navigate("/student-list")}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <FaArrowLeft className="inline mr-2" /> Öğrenci Listesine Dön
          </button>
        </div>
      </div>
    );
  }

  const {
    name,
    surname,
    student_number,
    student_class,
    branch,
    phone,
    email,
    gender,
    birth_date,
    tc_number,
    address,
    advisor_teacher,
    mother_name,
    mother_phone,
    mother_email,
    mother_address,
    father_name,
    period,
    father_phone,
    father_email,
    father_address,
    registration_date,
  } = isEditing ? editFormData : updatedStudent;

  // Ödeme planı analizi için varsayılan değerler
  const defaultPaymentPlan = {
    totalAmount: 0,
    installmentCount: 0,
    unpaidBills: [],
    paidInstallments: [],
  };

  // Ödeme planı parsing işlemi
  const parsePaymentPlan = (paymentPlanStr) => {
    // Null veya undefined kontrolü
    if (!paymentPlanStr) {
      return defaultPaymentPlan;
    }

    try {
      // Eğer string ise JSON'a çevir
      let paymentPlan = paymentPlanStr;

      // String kontrolü ekle
      if (typeof paymentPlanStr !== 'string') {
        console.warn("Payment plan is not a string:", typeof paymentPlanStr, paymentPlanStr);
        return defaultPaymentPlan;
      }

      let cleaned = paymentPlanStr.trim();
      const first = cleaned.indexOf('{');
      const last = cleaned.lastIndexOf('}');
      if (first !== -1 && last !== -1) cleaned = cleaned.slice(first, last + 1);
      cleaned = cleaned.replace(/\\"/g, '"');
      
      try {
        paymentPlan = JSON.parse(cleaned);
      } catch (innerError) {
        console.error("JSON parse hatası (temizlenmiş string):", innerError);
        return defaultPaymentPlan;
      }

      // Gerekli alanların var olduğundan emin ol ve kısmi ödeme alanlarını ekle
      const result = {
        totalAmount: paymentPlan.totalAmount || 0,
        installmentCount: paymentPlan.installmentCount || 0,
        unpaidBills: Array.isArray(paymentPlan.unpaidBills)
          ? paymentPlan.unpaidBills.map(bill => {
              // Bu taksit için ödenen toplam tutarı hesapla
              const paidAmount = Array.isArray(paymentPlan.paidInstallments) 
                ? paymentPlan.paidInstallments
                    .filter(paid => {
                      // appliedAmounts varsa, bu taksitten düşen tutarları topla
                      if (paid.appliedAmounts && Array.isArray(paid.appliedAmounts)) {
                        return paid.appliedAmounts.some(applied => applied.installmentId === bill.id);
                      }
                      // Eski yöntem - originalInstallmentId kontrolü
                      return paid.originalInstallmentId === bill.id || paid.id === bill.id;
                    })
                    .reduce((total, paid) => {
                      if (paid.appliedAmounts && Array.isArray(paid.appliedAmounts)) {
                        // appliedAmounts'tan bu taksit için olan tutarı bul
                        const appliedAmount = paid.appliedAmounts.find(applied => applied.installmentId === bill.id);
                        return total + (appliedAmount ? parseFloat(appliedAmount.amount) : 0);
                      }
                      // Eski yöntem - tam tutarı ekle
                      return total + parseFloat(paid.amount || 0);
                    }, 0)
                : 0;
              
              const originalAmount = parseFloat(bill.originalAmount || bill.amount);
              const currentAmount = parseFloat(bill.amount);
              
              return {
                ...bill,
                originalAmount: originalAmount, // Orijinal tutar
                paidAmount: paidAmount, // Ödenen tutar
                remainingAmount: currentAmount // Kalan tutar (mevcut amount)
              };
            })
          : [],
        paidInstallments: Array.isArray(paymentPlan.paidInstallments)
          ? paymentPlan.paidInstallments
          : [],
      };
      
      return result;
    } catch (error) {
      console.error("parsePaymentPlan error:", error);
      return defaultPaymentPlan;
    }
  };

  // Ödeme planı durumunu parse et
  const parsedPaymentPlan = parsePaymentPlan(updatedStudent?.payment_plan);

  // Ödenen ve bekleyen tutarları hesapla
  const totalAmount = parsedPaymentPlan?.totalAmount || 0;

  const totalUnpaidAmount =
    parsedPaymentPlan?.unpaidBills?.reduce(
      (total, bill) => total + parseFloat(bill.amount || 0),
      0
    ) || 0;

  const totalPaidAmount =
    parsedPaymentPlan?.paidInstallments?.reduce(
      (total, installment) => total + parseFloat(installment.amount || 0),
      0
    ) || 0;



  // Ödeme yüzdesini hesapla
  const paymentPercentage =
    totalAmount > 0 ? Math.round((totalPaidAmount / totalAmount) * 100) : 0;

  const handleUpdateStudent = (newData) => {
    // Önceki öğrenci verileriyle karşılaştırma yaparak aynı ödeme için tekrar istek yapmayı engelle
    const newPaymentData = newData._paymentData;

    // Eğer ödeme işlemi şu anda yapılıyorsa veya aynı ödeme zaten işlendiyse, çık
    if (processingPayment.current === true) {
      console.log("İşlem zaten devam ediyor, tekrar edilmeyecek");
      return;
    }

    // Eğer aynı timestamp'e sahip ödeme verisi daha önce işlendiyse işlemi atla
    if (
      lastProcessedPayment.current &&
      newPaymentData &&
      lastProcessedPayment.current === newPaymentData.timestamp
    ) {
      console.log(
        "Bu ödeme zaten işlendi, tekrarlanmayacak",
        newPaymentData.timestamp
      );
      return;
    }

    // İşlem kilidini aktif et
    processingPayment.current = true;

    if (newPaymentData) {
      // İşlenen ödemeyi kaydet
      lastProcessedPayment.current = newPaymentData.timestamp;
      console.log("Ödeme işleniyor, timestamp:", newPaymentData.timestamp);
    }

    // State'i güncelle
    setUpdatedStudent(newData);

    // Ödeme verisi varsa mali kayıt oluştur - HER MAKBUZ İÇİN AYRI KAYIT
    if (newData._paymentData) {
      try {
        const paymentData = newData._paymentData || {};
        const paymentDate = paymentData.paymentDate || new Date().toLocaleDateString("en-CA");
        
        // Yeni oluşan tüm makbuzlar için finansal kayıt oluştur
        const currentPaymentPlan = parsePaymentPlan(newData.payment_plan);
        const previousPaymentPlan = parsePaymentPlan(updatedStudent?.payment_plan);
        
        // Yeni eklenen makbuzları bul (önceki ile karşılaştırarak)
        const newInstallments = currentPaymentPlan.paidInstallments.filter(current => {
          return !previousPaymentPlan.paidInstallments.some(previous => 
            previous.id === current.id
          );
        });
        
        // Her yeni makbuz için ayrı finansal kayıt oluştur
        const createFinancialRecords = async () => {
          
          for (const installment of newInstallments) {
            const uniqueId = `payment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            
            const financialRecordData = {
              description: `${newData.name} ${newData.surname} - ${
                installment.partialPayment ? "Kısmi Ödeme" : 
                (paymentData.type === "taksit" ? "Taksit Ödemesi" : "Nakit ödeme")
              }`,
              amount: installment.amount,
              transaction_date: paymentDate,
              payment_method: paymentData.method || "nakit",
              status: "completed",
              notes: `${installment.notes || paymentData.notes || "Ödeme planı güncellemesi"} [ID: ${uniqueId}]`,
              category: "Öğrenci Ödemesi",
            };
            
            // Duplikasyon kontrolünü kaldır - her makbuz için kayıt oluştur
            // Unique ID zaten var, gerçek duplikasyon olmaz
            await axios.post(
              "http://esnakademi.com/api/financial-records",
              financialRecordData,
              { headers: getRequestHeaders() }
            );
          }
        };
        
        createFinancialRecords()
          .then(() => {
            // Tüm mali kayıtlar oluşturuldu
          })
          .catch((error) => {
            // Mali kayıt hatası
          })
          .finally(() => {
            setTimeout(() => {
              processingPayment.current = false;
            }, 1000);
          });
      } catch (error) {
        processingPayment.current = false; // Hata durumunda kilidi kaldır
      }
    } else {
      // Ödeme verisi yoksa kilidi hemen kaldır
      processingPayment.current = false;
    }

    // UI güncelleme bildirimi
    toast.success("Ödeme işlemi tamamlandı!");
  };

  const handleDownloadPaymentPlan = () => {
    try {
      // Başlıklar
      const headers = [
        "Öğrenci Adı",
        "Öğrenci Soyadı",
        "Toplam Tutar",
        "Taksit No",
        "Son Ödeme Tarihi",
        "Taksit Tutarı",
        "Durum",
      ];

      // Taksitleri birleştir
      const allInstallments = [
        ...(parsedPaymentPlan?.unpaidBills || []).map((bill) => ({
          ...bill,
          status: "Ödenmedi",
        })),
        ...(parsedPaymentPlan?.paidInstallments || []).map((bill) => ({
          ...bill,
          status: "Ödendi",
        })),
      ].sort((a, b) => a.id - b.id);

      const rows = allInstallments.map((inst) => [
        name,
        surname,
        totalAmount,
        inst.id,
        inst.dueDate,
        inst.amount,
        inst.status,
      ]);

      // Sheet oluştur
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Ödeme Planı");

      const fileName = `odeme_plani_${name}_${surname}_${new Date()
        .toLocaleDateString("tr-TR")
        .replace(/\./g, "-")}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error("Ödeme planı excel indirme hatası:", error);
      toast.error("Ödeme planı indirilirken bir hata oluştu!");
    }
  };

  // Öğrenciyi silme işlemi
  const handleDeleteStudent = async () => {
    if (!updatedStudent?.id) {
      toast.error("Öğrenci bilgisi bulunamadı!");
      return;
    }

    // Finansal kayıtları da silinsin mi diye sor
    const deleteFinancialRecords = window.confirm(
      `${updatedStudent.name} ${updatedStudent.surname} isimli öğrencinin alınmış ödemelerini de finansal sayfasından silmek istiyor musunuz?\n\n` +
      `• EVET: Öğrenci ve tüm ödemeleri silinir\n` +
      `• HAYIR: Sadece öğrenci silinir, ödemeler finansal sayfada kalır`
    );

    setLoading(true);
    try {
      // Eğer finansal kayıtlar da silinecekse, mevcut makbuz silme fonksiyonunu kullan
      if (deleteFinancialRecords) {
        const paymentPlan = parsePaymentPlan(updatedStudent?.payment_plan);
        const paidInstallments = paymentPlan?.paidInstallments || [];
        
        if (paidInstallments.length > 0) {
          const deleteToast = toast.loading(`${paidInstallments.length} adet makbuz siliniyor...`);
          
          // Her makbuzu tek tek sil (mevcut handleDeletePayment mantığını kullan)
          for (const installment of paidInstallments) {
            try {
              // Financial records'tan ilgili kaydı bul ve sil
              const paymentDescription = `${updatedStudent.name} ${updatedStudent.surname}`;
              
              const timestamp2 = new Date().getTime();
              const financialResponse = await axios.get(
                `http://esnakademi.com/api/financial-records?_t=${timestamp2}`,
                { headers: getRequestHeaders() }
              );
              
              const financialRecords = financialResponse.data || [];
              
              // İlgili financial record'ları bul
              const matchingRecords = financialRecords.filter(record => {
                if (!record.description || !record.description.includes(paymentDescription)) {
                  return false;
                }
                
                const recordDate = new Date(record.transaction_date).toISOString().split('T')[0];
                const installmentDate = new Date(installment.paymentDate || installment.dueDate).toISOString().split('T')[0];
                
                return recordDate === installmentDate && 
                       Math.abs(parseFloat(record.amount) - parseFloat(installment.amount)) < 0.01;
              });
              
              // Financial record'ları sil
              for (const record of matchingRecords) {
                await axios.delete(
                  `http://esnakademi.com/api/financial-records?id=${record.id}`,
                  { headers: getRequestHeaders() }
                );
              }
            } catch (paymentError) {
              console.warn(`Makbuz ${installment.id} silinirken hata (devam edildi):`, paymentError);
            }
          }
          
          toast.dismiss(deleteToast);
          toast.success(`${paidInstallments.length} adet makbuz silindi`);
        }
      }

      // API'ye silme isteği gönder
      await axios.delete(
        `http://esnakademi.com/api/students/delete?id=${updatedStudent.id}`,
        {
          headers: getRequestHeaders(),
        }
      );

      toast.success("Öğrenci başarıyla silindi!");
      // Öğrenci listesine geri dön
      localStorage.removeItem("currentStudentId");
      navigate("/student-list");
    } catch (error) {
      console.error("Öğrenci silme hatası:", error);
      toast.error("Öğrenci silinirken bir hata oluştu!");
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  // Basit ödeme silme fonksiyonu
  const handleDeletePayment = async (installment) => {
    if (!window.confirm(
      `${installment.id} nolu taksit ödemesini silmek istediğinizden emin misiniz?`
    )) {
      return;
    }

    const loadingToast = toast.loading('Makbuz siliniyor...');
    setLoading(true);
    try {
      // 0. Backend'de makbuz kaydını sil
      try {
        // Önce öğrenciye ait makbuzları getir
        const receiptsResponse = await axios.get(`http://esnakademi.com/api/receipts/student/${updatedStudent.id}`, {
          headers: getRequestHeaders()
        });
        
        const receipts = receiptsResponse.data || [];
        const receiptNumber = `${updatedStudent.student_number || "00000000"}-${String(installment.id).padStart(3,"0")}`;
        
        // İlgili makbuzu bul
        const targetReceipt = receipts.find(receipt => receipt.receipt_number === receiptNumber);
        
        if (targetReceipt) {
          // Backend'den makbuz kaydını sil
          await axios.delete(`http://esnakademi.com/api/receipts/${targetReceipt.id}`, {
            headers: getRequestHeaders()
          });
          
          toast.success("Makbuz backend'den silindi!");
        } else {
          console.warn("Backend'de makbuz bulunamadı:", receiptNumber);
        }
      } catch (receiptError) {
        console.warn("Backend makbuz silme hatası (devam edildi):", receiptError);
        // Backend silme başarısız olsa bile devam et
      }

      // 1. Financial records'tan ilgili kaydı bul ve sil
      const paymentDescription = `${updatedStudent.name} ${updatedStudent.surname}`;
      
      const timestamp2 = new Date().getTime();
      const financialResponse = await axios.get(
        `http://esnakademi.com/api/financial-records?_t=${timestamp2}`,
        { headers: getRequestHeaders() }
      );
      
      const financialRecords = financialResponse.data || [];
      
      const relatedRecord = financialRecords.find(record => {
        const recordDate = record.transaction_date;
        const installmentDate = installment.paymentDate || installment.dueDate;
        const recordAmount = Math.abs(parseFloat(record.amount));
        const installmentAmount = parseFloat(installment.amount);
        
        return (
          record.description.includes(paymentDescription) &&
          recordDate === installmentDate &&
          Math.abs(recordAmount - installmentAmount) < 0.01
        );
      });

      if (relatedRecord) {
        await axios.delete(
          `http://esnakademi.com/api/financial-records?id=${relatedRecord.id}`,
          { headers: getRequestHeaders() }
        );
      }

      // 2. Payment plan'ı güncelle
      const updatedPaymentPlan = { ...parsedPaymentPlan };
      
      // Silinen ödemeyi paid installments'tan çıkar
      updatedPaymentPlan.paidInstallments = updatedPaymentPlan.paidInstallments.filter(
        paid => paid.id !== installment.id
      );
      
      const deletedAmount = parseFloat(installment.amount);
      const originalInstallmentId = installment.originalInstallmentId || installment.id;
      
      // appliedAmounts bilgisi varsa, her taksite doğru tutarı iade et
      if (installment.appliedAmounts && Array.isArray(installment.appliedAmounts)) {
        // Her uygulanan tutarı ilgili taksite iade et
        installment.appliedAmounts.forEach(applied => {
          const bill = updatedPaymentPlan.unpaidBills.find(b => b.id === applied.installmentId);
          if (bill) {
            // Mevcut taksite tutarı ekle
            const currentAmount = parseFloat(bill.amount || 0);
            bill.amount = currentAmount + parseFloat(applied.amount);
            delete bill.paidAmount; // paidAmount'u temizle
          } else {
            // Bu taksit için unpaid bill yoksa, yeni oluştur
            const newUnpaidBill = {
              id: applied.installmentId,
              amount: parseFloat(applied.amount),
              dueDate: installment.dueDate || installment.paymentDate,
              notes: `${applied.installmentId}. Taksit`
            };
            updatedPaymentPlan.unpaidBills.push(newUnpaidBill);
          }
        });
        
        // Tarih sırasına göre sırala
        updatedPaymentPlan.unpaidBills.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
      } else {
        // Eski yöntem - geriye uyumluluk için
        // Unpaid bills'de aynı ID'li taksit var mı kontrol et
        let relatedUnpaidBill = updatedPaymentPlan.unpaidBills.find(bill => bill.id === originalInstallmentId);
        
        if (relatedUnpaidBill) {
          // Var olan unpaid bill'e silinen tutarı ekle
          const currentAmount = parseFloat(relatedUnpaidBill.amount || 0);
          const newAmount = currentAmount + deletedAmount;
          
          // Orijinal taksit tutarını kontrol et
          const originalAmount = parseFloat(installment.originalAmount || installment.amount);
          
          // Eğer yeni tutar orijinal taksit tutarını aşıyorsa, fazla kısmı sonraki taksitlere dağıt
          if (newAmount > originalAmount) {
            const excessAmount = newAmount - originalAmount;
            
            // Mevcut taksiti orijinal tutarına ayarla
            relatedUnpaidBill.amount = originalAmount;
            
            // Fazla tutarı sonraki taksitlere dağıt
            let remainingExcess = excessAmount;
            const nextBills = updatedPaymentPlan.unpaidBills
              .filter(bill => bill.id > originalInstallmentId)
              .sort((a, b) => a.id - b.id);
            
            for (const nextBill of nextBills) {
              if (remainingExcess <= 0) break;
              
              const nextBillAmount = parseFloat(nextBill.amount);
              if (remainingExcess >= nextBillAmount) {
                // Bu taksiti tamamen kapat
                nextBill.amount = 0;
                remainingExcess -= nextBillAmount;
              } else {
                // Bu taksiti kısmen azalt
                nextBill.amount = nextBillAmount - remainingExcess;
                remainingExcess = 0;
              }
            }
            
            // Sıfır tutarlı taksitleri kaldır
            updatedPaymentPlan.unpaidBills = updatedPaymentPlan.unpaidBills.filter(bill => parseFloat(bill.amount) > 0);
          } else {
            // Normal durum - sadece tutarı artır
            relatedUnpaidBill.amount = newAmount;
          }
          
          // paidAmount'u temizle
          delete relatedUnpaidBill.paidAmount;
        } else {
          // Bu taksit için unpaid bill yoksa, silinen tutarı ekle
          const originalAmount = installment.originalAmount;
          
          const newUnpaidBill = {
            id: originalInstallmentId,
            amount: deletedAmount, // Silinen tutarı ekle
            originalAmount: originalAmount,
            dueDate: installment.dueDate || installment.paymentDate,
            notes: `${originalInstallmentId}. Taksit`
          };
          
          updatedPaymentPlan.unpaidBills.push(newUnpaidBill);
          updatedPaymentPlan.unpaidBills.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
        }
      }
      
      // Tüm unpaid bills'deki paidAmount'ları temizle
      updatedPaymentPlan.unpaidBills.forEach(bill => {
        delete bill.paidAmount; // paidAmount'u temizle
      });
      
      // UYARI: totalAmount (sözleşme miktarı) ASLA değiştirilmemelidir!
      // totalAmount sadece manuel sözleşme düzenlemelerinde değişebilir.
      // Makbuz silme işlemi sözleşme miktarını etkilemez.
      
      // totalAmount'u koruyoruz - değiştirmiyoruz
      // updatedPaymentPlan.totalAmount zaten mevcut değerini koruyor

      // 3. Öğrenci verisini güncelle
      const paymentPlanJson = JSON.stringify(updatedPaymentPlan);
      
      await axios.put(`http://esnakademi.com/api/students/update?id=${updatedStudent.id}`, {
        id: updatedStudent.id,
        name: updatedStudent.name,
        surname: updatedStudent.surname,
        student_number: updatedStudent.student_number,
        student_class: updatedStudent.student_class,
        period: updatedStudent.period || "",
        registration_date: updatedStudent.registration_date || "",
        branch: updatedStudent.branch || "",
        phone: updatedStudent.phone || "",
        email: updatedStudent.email || "",
        photo_url: updatedStudent.photo_url || "",
        gender: updatedStudent.gender || "",
        birth_date: updatedStudent.birth_date || "",
        tc_number: updatedStudent.tc_number || "",
        address: updatedStudent.address || "",
        advisor_teacher: updatedStudent.advisor_teacher || "",
        mother_name: updatedStudent.mother_name || "",
        mother_phone: updatedStudent.mother_phone || "",
        mother_email: updatedStudent.mother_email || "",
        mother_address: updatedStudent.mother_address || "",
        father_name: updatedStudent.father_name || "",
        father_phone: updatedStudent.father_phone || "",
        father_email: updatedStudent.father_email || "",
        father_address: updatedStudent.father_address || "",
        payment_plan: paymentPlanJson
      }, { headers: getRequestHeaders() });

      setUpdatedStudent(prev => ({
        ...prev,
        payment_plan: paymentPlanJson
      }));

      toast.dismiss(loadingToast);
      toast.success("Ödeme başarıyla silindi");
      
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error("Ödeme silme hatası:", error);
      toast.error("Ödeme silinirken bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  // Tek bir taksit için makbuz şablonunu göster
  const handlePrintReceipt = async (installment) => {
    try {
      // Kalan toplam borç tutarını hesapla
      const remainingTotalDebt = parsedPaymentPlan?.unpaidBills?.reduce(
        (total, bill) => total + parseFloat(bill.amount || 0), 0
      ) || 0;

      // Makbuz verilerini hazırla
      const makbuzData = {
        student: updatedStudent,
        installment: installment,
        paymentDate: installment.paymentDate || installment.dueDate,
        receiptDate: new Date().toISOString().split("T")[0],
        receiptNumber: `${updatedStudent.student_number || "00000000"}-${String(installment.id).padStart(3,"0")}`,
        totalAmount: installment.amount,
        paymentMethod: installment.paymentMethod || "NAKİT",
        paymentPerson: installment.paymentPerson || "anne", // Varsayılan olarak anne
        paymentPersonSurname: installment.paymentPersonSurname || "", // Ödeme yapan kişinin soyadı
        remainingTotalDebt: remainingTotalDebt,
        receiptUser: installment.receiptUser // Makbuzu alan kullanıcının adı (varsa)
      };



      // Makbuz verilerini state'e kaydet ve modal'ı aç
      setMakbuzData(makbuzData);
      setIsMakbuzModalOpen(true);
      
    } catch (err) {
      console.error("Makbuz oluşturma hatası:", err);
      toast.error("Makbuz oluşturulurken hata oluştu!");
    }
  };

  // Ödeme planı oluşturucu (aylık)
  const generatePaymentPlan = (totalAmount, monthlyInstallmentAmount, startDate) => {
    const [y, m, d] = startDate.split("-").map((v) => parseInt(v, 10));
    const startUTC = new Date(Date.UTC(y, m - 1, d));

    const count = Math.ceil(totalAmount / monthlyInstallmentAmount);
    const remainder = totalAmount % monthlyInstallmentAmount;

    return Array.from({ length: count }).map((_, idx) => {
      const dueUTC = new Date(Date.UTC(startUTC.getUTCFullYear(), startUTC.getUTCMonth() + idx, startUTC.getUTCDate()));
      const amount = idx === count - 1 && remainder > 0 ? remainder : monthlyInstallmentAmount;
      return { id: idx + 1, amount, dueDate: dueUTC.toISOString().split("T")[0] };
    });
  };

  const openContractEdit = () => {
    setIsContractModalOpen(true);
  };

  const closeContractEdit = () => setIsContractModalOpen(false);

  const handleSaveContractEdit = async (contractData) => {
    if (!updatedStudent) return;
    
    const totalAmount = parseFloat(contractData.totalAmount);
    let newPlan;

    // Manuel mod kontrolü
    if (contractData.isManual && contractData.manualInstallments) {
      // Manuel taksitleri unpaidBills formatına çevir
      const unpaidBills = contractData.manualInstallments.map(inst => ({
        id: inst.id,
        amount: inst.amount,
        dueDate: inst.dueDate,
        notes: `${inst.id}. Taksit`
      }));

      newPlan = {
        totalAmount: totalAmount,
        installmentCount: unpaidBills.length,
        startDate: contractData.startDate,
        unpaidBills,
        paidInstallments: [],
        contractMaker: contractData.contractMaker,
        updated_at: new Date().toISOString(),
        isManual: true
      };
    } else {
      // Otomatik mod
      const unpaidBills = generatePaymentPlan(
        totalAmount,
        parseFloat(contractData.monthlyInstallmentAmount),
        contractData.startDate
      );

      newPlan = {
        totalAmount: totalAmount,
        monthlyInstallmentAmount: parseFloat(contractData.monthlyInstallmentAmount),
        installmentCount: unpaidBills.length,
        startDate: contractData.startDate,
        unpaidBills,
        paidInstallments: [],
        contractMaker: contractData.contractMaker,
        updated_at: new Date().toISOString(),
        isManual: false
      };
    }

    try {
      setLoading(true);
      await axios.put(
        `http://esnakademi.com/api/students/update?id=${updatedStudent.id}`,
        {
          ...updatedStudent,
          payment_plan: JSON.stringify(newPlan),
        },
        { headers: getRequestHeaders() }
      );
      
      // Hem updatedStudent hem de editFormData'yı güncelle
      const updatedStudentData = { ...updatedStudent, payment_plan: JSON.stringify(newPlan) };
      
      setUpdatedStudent(updatedStudentData);
      
      // Eğer düzenleme modundaysa editFormData'yı da güncelle
      if (isEditing) {
        setEditFormData(prev => ({ ...prev, payment_plan: JSON.stringify(newPlan) }));
      }
      
      toast.success("Sözleşme güncellendi");
    } catch (err) {
      console.error(err);
      toast.error("Sözleşme güncellenirken hata oluştu");
    } finally {
      setLoading(false);
      setIsContractModalOpen(false);
    }
  };

  const sanitizeDate = (d) => (d && d !== '0000-00-00') ? d : '';

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-6 lg:py-8 px-2 sm:px-4 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Üst Başlık & Geri Dönüş */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
          <button
            onClick={() => navigate("/student-list")}
            className="self-start px-3 sm:px-4 py-2 text-sm sm:text-base bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors flex items-center"
          >
            <FaArrowLeft className="mr-1 sm:mr-2 text-sm sm:text-base" />{" "}
            Öğrenci Listesine Dön
          </button>
          <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-800 flex items-center">
            {gender === "male" ? (
              <FaMale className="mr-1 sm:mr-2 text-blue-500 text-lg sm:text-2xl lg:text-3xl" />
            ) : gender === "female" ? (
              <FaFemale className="mr-1 sm:mr-2 text-pink-500 text-lg sm:text-2xl lg:text-3xl" />
            ) : (
              <FaUser className="mr-1 sm:mr-2 text-gray-500 text-lg sm:text-2xl lg:text-3xl" />
            )}
            <span className="break-words">
              {name} {surname}
            </span>
          </h1>
        </div>

        {/* Ana İçerik */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {/* Üst Bilgi Kartı */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-3 sm:p-4 lg:p-6 text-white">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 lg:gap-0">
              <div className="w-full lg:w-auto">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold mb-2 break-words">
                  {name} {surname}
                </h2>
                <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                  <div className="flex items-center">
                    <FaIdCard className="mr-1 sm:mr-2 text-xs sm:text-sm" />
                    <span>Öğrenci No: {student_number}</span>
                  </div>
                  <div className="flex items-center">
                    <FaSchool className="mr-1 sm:mr-2 text-xs sm:text-sm" />
                    <span>
                      {student_class}. Sınıf {branch && `- ${branch} Şubesi`}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <FaCalendarAlt className="mr-1 sm:mr-2 text-xs sm:text-sm" />
                    <span>{period || "2023-2024"} Dönemi</span>
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-auto">
                <div className="flex flex-col sm:flex-row lg:flex-wrap gap-2 sm:gap-2 lg:gap-2">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleSaveEdit}
                        className="w-full sm:w-auto px-3 sm:px-4 py-2 text-xs sm:text-sm bg-green-500 rounded-md hover:bg-green-600 transition-colors flex items-center justify-center"
                      >
                        <FaEdit className="mr-1 sm:mr-2 text-xs sm:text-sm" /> Kaydet
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="w-full sm:w-auto px-3 sm:px-4 py-2 text-xs sm:text-sm bg-gray-500 rounded-md hover:bg-gray-600 transition-colors flex items-center justify-center"
                      >
                        <FaTimes className="mr-1 sm:mr-2 text-xs sm:text-sm" /> İptal
                      </button>
                      <button
                        onClick={openContractEdit}
                        className="w-full sm:w-auto px-3 sm:px-4 py-2 text-xs sm:text-sm bg-purple-500 rounded-md hover:bg-purple-600 transition-colors flex items-center justify-center"
                      >
                        <FaEdit className="mr-1 sm:mr-2 text-xs sm:text-sm" /> Sözleşme
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={openModal}
                        disabled={paymentLoading}
                        className={`w-full sm:w-auto px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-md transition-colors flex items-center justify-center ${
                          paymentLoading
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-green-500 hover:bg-green-600"
                        }`}
                      >
                        {paymentLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="hidden sm:inline">İşlem Yapılıyor...</span>
                            <span className="sm:hidden">İşlem...</span>
                          </>
                        ) : (
                          <>
                            <FaMoneyBillWave className="mr-1 sm:mr-2 text-xs sm:text-sm" />
                            <span className="hidden sm:inline">Ödeme Al</span>
                            <span className="sm:hidden">Ödeme</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleDownloadPaymentPlan}
                        className="w-full sm:w-auto px-3 sm:px-4 py-2 text-xs sm:text-sm bg-yellow-500 rounded-md hover:bg-yellow-600 transition-colors flex items-center justify-center"
                      >
                        <FaFileDownload className="mr-1 sm:mr-2 text-xs sm:text-sm" />
                        <span className="hidden lg:inline">
                          Ödeme Planı İndir
                        </span>
                        <span className="lg:hidden">Plan İndir</span>
                      </button>
                      {userRole === "admin" && (
                        <button
                          onClick={handleEditStudent}
                          className="w-full sm:w-auto px-3 sm:px-4 py-2 text-xs sm:text-sm bg-blue-500 rounded-md hover:bg-blue-600 transition-colors flex items-center justify-center"
                        >
                          <FaEdit className="mr-1 sm:mr-2 text-xs sm:text-sm" />{" "}
                          Düzenle
                        </button>
                      )}
                      {userRole === "admin" && (
                        <button
                          onClick={() => setDeleteConfirmOpen(true)}
                          className="w-full sm:w-auto px-3 sm:px-4 py-2 text-xs sm:text-sm bg-red-500 rounded-md hover:bg-red-600 transition-colors flex items-center justify-center"
                        >
                          <FaTrash className="mr-1 sm:mr-2 text-xs sm:text-sm" />{" "}
                          Sil
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* İçerik Alanı */}
          <div className="p-3 sm:p-4 lg:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Sol Kolon */}
              <div>
                {/* Kişisel Bilgiler */}
                <div className="mb-4 sm:mb-6 bg-gray-50 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                    <FaUser className="mr-1 sm:mr-2 text-blue-600 text-sm sm:text-base" />
                    Kişisel Bilgiler
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="flex items-start">
                      <FaUser className="mt-1 mr-2 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Ad</p>
                        {isEditing ? (
                          <input
                            type="text"
                            name="name"
                            value={editFormData.name || ""}
                            onChange={handleFormChange}
                            className="w-full p-2 border rounded-md capitalize"
                          />
                        ) : (
                          <p className="font-medium">
                            {name || "Belirtilmemiş"}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start">
                      <FaUser className="mt-1 mr-2 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Soyad</p>
                        {isEditing ? (
                          <input
                            type="text"
                            name="surname"
                            value={editFormData.surname || ""}
                            onChange={handleFormChange}
                            className="w-full p-2 border rounded-md capitalize"
                          />
                        ) : (
                          <p className="font-medium">
                            {surname || "Belirtilmemiş"}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start">
                      <FaIdCard className="mt-1 mr-2 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">TC Kimlik No</p>
                        {isEditing ? (
                          <input
                            type="text"
                            name="tc_number"
                            value={editFormData.tc_number || ""}
                            onChange={handleFormChange}
                            className="w-full p-2 border rounded-md"
                          />
                        ) : (
                          <p className="font-medium">
                            {tc_number || "Belirtilmemiş"}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start">
                      <FaCalendarAlt className="mt-1 mr-2 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Doğum Tarihi</p>
                        {isEditing ? (
                          <input
                            type="date"
                            name="birth_date"
                            value={sanitizeDate(editFormData.birth_date)}
                            onChange={handleFormChange}
                            className="w-full p-2 border rounded-md"
                            min="1900-01-01"
                            max="2099-12-31"
                          />
                        ) : (
                          <p className="font-medium">
                            {formatDateDisplay(birth_date)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start">
                      <FaCalendarAlt className="mt-1 mr-2 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Kayıt Tarihi</p>
                        {isEditing ? (
                          <input
                            type="date"
                            name="registration_date"
                            value={sanitizeDate(editFormData.registration_date)}
                            onChange={handleFormChange}
                            className="w-full p-2 border rounded-md"
                            min="1900-01-01"
                            max="2099-12-31"
                          />
                        ) : (
                          <p className="font-medium">
                            {formatDateDisplay(registration_date)}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {updatedStudent?.created_by_username && (
                      <div className="flex items-start">
                        <FaUserPlus className="mt-1 mr-2 text-blue-500" />
                        <div>
                          <p className="text-sm text-gray-500">Kaydı Yapan</p>
                          <p className="font-medium text-blue-600">
                            {updatedStudent.created_by_username}
                          </p>
                          <p className="text-xs text-gray-400">
                            Kayıt Kullanıcısı
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-start">
                      <FaPhone className="mt-1 mr-2 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Telefon</p>
                        {isEditing ? (
                          <input
                            type="tel"
                            name="phone"
                            value={editFormData.phone || ""}
                            onChange={handleFormChange}
                            className="w-full p-2 border rounded-md"
                            placeholder="5** *** ** **"
                            maxLength="16"
                            title="Telefon numarası giriniz (5** *** ** **)"
                          />
                        ) : (
                          <p className="font-medium">
                            {phone ? phone : "Belirtilmemiş"}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start">
                      <FaEnvelope className="mt-1 mr-2 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">E-posta</p>
                        {isEditing ? (
                          <input
                            type="email"
                            name="email"
                            value={editFormData.email || ""}
                            onChange={handleFormChange}
                            className="w-full p-2 border rounded-md"
                          />
                        ) : (
                          <p className="font-medium">
                            {email || "Belirtilmemiş"}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start col-span-2">
                      <FaHome className="mt-1 mr-2 text-gray-500" />
                      <div className="w-full">
                        <p className="text-sm text-gray-500">Adres</p>
                        {isEditing ? (
                          <textarea
                            name="address"
                            value={editFormData.address || ""}
                            onChange={handleFormChange}
                            className="w-full p-2 border rounded-md capitalize"
                            rows="2"
                          ></textarea>
                        ) : (
                          <p className="font-medium">
                            {address || "Belirtilmemiş"}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start">
                      <FaSchool className="mt-1 mr-2 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Sınıf</p>
                        {isEditing ? (
                          <select
                            name="student_class"
                            value={editFormData.student_class || ""}
                            onChange={handleFormChange}
                            className="w-full p-2 border rounded-md"
                          >
                            <option value="">Sınıf Seçin</option>
                            {classOptions.map(c => (
                              <option key={c.id} value={c.class_level}>
                                {c.class_level}. Sınıf
                              </option>
                            ))}
                          </select>
                        ) : (
                          <p className="font-medium">
                            {student_class || "Belirtilmemiş"}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start">
                      <FaSchool className="mt-1 mr-2 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Şube</p>
                        {isEditing ? (
                          <select
                            name="branch"
                            value={editFormData.branch || ""}
                            onChange={handleFormChange}
                            className="w-full p-2 border rounded-md"
                            disabled={!editFormData.student_class || branchOptions.length === 0 || branchLoading}
                          >
                            <option value="">
                              {!editFormData.student_class 
                                ? "Önce sınıf seçin" 
                                : branchLoading
                                ? "Şubeler yükleniyor..."
                                : branchOptions.length === 0 
                                ? "Bu sınıf için şube yok" 
                                : "Şube Seçin"
                              }
                            </option>
                            {branchOptions.map(b => (
                              <option key={b.id} value={b.branch_name}>
                                {b.branch_name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <p className="font-medium">
                            {branch || "Belirtilmemiş"}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <FaSchool className="mt-1 mr-2 text-green-500" />
                      <div>
                        <p className="text-sm text-gray-500">Okul</p>
                        {isEditing ? (
                          <select
                            name="school"
                            value={editFormData.school || ""}
                            onChange={handleFormChange}
                            className="w-full p-2 border rounded-md"
                          >
                            <option value="">Okul Seçin</option>
                            <option value="Cumhuriyet Ortaokulu">Cumhuriyet Ortaokulu</option>
                            <option value="31 Ağustos Ortaokulu">31 Ağustos Ortaokulu</option>
                            <option value="İmam Hatip Ortaokulu">İmam Hatip Ortaokulu</option>
                            <option value="Halime Ali Kaya Ortaokulu">Halime Ali Kaya Ortaokulu</option>
                            <option value="Diğer">Diğer</option>
                          </select>
                        ) : (
                          <p className="font-medium text-green-700">
                            {updatedStudent?.school || "Belirtilmemiş"}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start col-span-2">
                      <FaUserTie className="mt-1 mr-2 text-gray-500" />
                      <div className="w-full">
                        <p className="text-sm text-gray-500">
                          Sözleşmeyi Yapan
                        </p>
                        {isEditing ? (
                          <input
                            type="text"
                            name="advisor_teacher"
                            value={editFormData.advisor_teacher || ""}
                            onChange={handleFormChange}
                            className="w-full p-2 border rounded-md capitalize"
                          />
                        ) : (
                          <p className="font-medium">
                            {advisor_teacher || "Belirtilmemiş"}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Aile Bilgileri */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                    <FaUserFriends className="mr-1 sm:mr-2 text-blue-600 text-sm sm:text-base" />
                    Aile Bilgileri
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:gap-6">
                    {/* Anne Bilgileri */}
                    <div className="p-3 sm:p-4 border border-gray-200 rounded-lg bg-white">
                      <h4 className="font-semibold text-gray-700 text-sm sm:text-base mb-2 sm:mb-3 border-b pb-2">
                        Anne Bilgileri
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div className="flex items-start">
                          <FaUser className="mt-1 mr-2 text-gray-500" />
                          <div className="w-full">
                            <p className="text-sm text-gray-500">Anne Adı</p>
                            {isEditing ? (
                              <input
                                type="text"
                                name="mother_name"
                                value={editFormData.mother_name || ""}
                                onChange={handleFormChange}
                                className="w-full p-2 border rounded-md capitalize"
                              />
                            ) : (
                              <p className="font-medium">
                                {mother_name || "Belirtilmemiş"}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-start">
                          <FaPhone className="mt-1 mr-2 text-gray-500" />
                          <div className="w-full">
                            <p className="text-sm text-gray-500">Telefon</p>
                            {isEditing ? (
                              <input
                                type="tel"
                                name="mother_phone"
                                value={editFormData.mother_phone || ""}
                                onChange={handleFormChange}
                                className="w-full p-2 border rounded-md"
                                placeholder="5** *** ** **"
                                maxLength="16"
                                title="Telefon numarası giriniz (5** *** ** **)"
                              />
                            ) : (
                              <p className="font-medium">
                                {mother_phone ? formatPhone(mother_phone) : "Belirtilmemiş"}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-start">
                          <FaEnvelope className="mt-1 mr-2 text-gray-500" />
                          <div className="w-full">
                            <p className="text-sm text-gray-500">E-posta</p>
                            {isEditing ? (
                              <input
                                type="email"
                                name="mother_email"
                                value={editFormData.mother_email || ""}
                                onChange={handleFormChange}
                                className="w-full p-2 border rounded-md"
                              />
                            ) : (
                              <p className="font-medium">
                                {mother_email || "Belirtilmemiş"}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-start">
                          <FaHome className="mt-1 mr-2 text-gray-500" />
                          <div className="w-full">
                            <p className="text-sm text-gray-500">Adres</p>
                            {isEditing ? (
                              <textarea
                                name="mother_address"
                                value={editFormData.mother_address || ""}
                                onChange={handleFormChange}
                                className="w-full p-2 border rounded-md capitalize"
                                rows="2"
                              ></textarea>
                            ) : (
                              <p className="font-medium">
                                {mother_address || "Belirtilmemiş"}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Baba Bilgileri */}
                    <div className="p-3 sm:p-4 border border-gray-200 rounded-lg bg-white">
                      <h4 className="font-semibold text-gray-700 text-sm sm:text-base mb-2 sm:mb-3 border-b pb-2">
                        Baba Bilgileri
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div className="flex items-start">
                          <FaUser className="mt-1 mr-2 text-gray-500" />
                          <div className="w-full">
                            <p className="text-sm text-gray-500">Baba Adı</p>
                            {isEditing ? (
                              <input
                                type="text"
                                name="father_name"
                                value={editFormData.father_name || ""}
                                onChange={handleFormChange}
                                className="w-full p-2 border rounded-md capitalize"
                              />
                            ) : (
                              <p className="font-medium">
                                {father_name || "Belirtilmemiş"}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-start">
                          <FaPhone className="mt-1 mr-2 text-gray-500" />
                          <div className="w-full">
                            <p className="text-sm text-gray-500">Telefon</p>
                            {isEditing ? (
                              <input
                                type="tel"
                                name="father_phone"
                                value={editFormData.father_phone || ""}
                                onChange={handleFormChange}
                                className="w-full p-2 border rounded-md"
                                placeholder="5** *** ** **"
                                maxLength="16"
                                title="Telefon numarası giriniz (5** *** ** **)"
                              />
                            ) : (
                              <p className="font-medium">
                                {father_phone ? formatPhone(father_phone) : "Belirtilmemiş"}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-start">
                          <FaEnvelope className="mt-1 mr-2 text-gray-500" />
                          <div className="w-full">
                            <p className="text-sm text-gray-500">E-posta</p>
                            {isEditing ? (
                              <input
                                type="email"
                                name="father_email"
                                value={editFormData.father_email || ""}
                                onChange={handleFormChange}
                                className="w-full p-2 border rounded-md"
                              />
                            ) : (
                              <p className="font-medium">
                                {father_email || "Belirtilmemiş"}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-start">
                          <FaHome className="mt-1 mr-2 text-gray-500" />
                          <div className="w-full">
                            <p className="text-sm text-gray-500">Adres</p>
                            {isEditing ? (
                              <textarea
                                name="father_address"
                                value={editFormData.father_address || ""}
                                onChange={handleFormChange}
                                className="w-full p-2 border rounded-md capitalize"
                                rows="2"
                              ></textarea>
                            ) : (
                              <p className="font-medium">
                                {father_address || "Belirtilmemiş"}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Ödeme Sorumlusu Bilgileri */}
                    <div className="p-3 sm:p-4 border border-gray-200 rounded-lg bg-white">
                      <h4 className="font-semibold text-gray-700 text-sm sm:text-base mb-2 sm:mb-3 border-b pb-2">
                        Ödeme Sorumlusu Bilgileri
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div className="flex items-start">
                          <FaUserTie className="mt-1 mr-2 text-gray-500" />
                          <div className="w-full">
                            <p className="text-sm text-gray-500">Ödeme Sorumlusu</p>
                            {isEditing ? (
                              <select
                                name="payment_responsible"
                                value={editFormData.payment_responsible || ""}
                                onChange={handleFormChange}
                                className="w-full p-2 border rounded-md"
                              >
                                <option value="">Seçiniz</option>
                                <option value="mother">Anne</option>
                                <option value="father">Baba</option>
                              </select>
                            ) : (
                              <p className="font-medium">
                                {updatedStudent?.payment_responsible === "mother" ? "Anne" : 
                                 updatedStudent?.payment_responsible === "father" ? "Baba" : 
                                 updatedStudent?.payment_responsible === "anne" ? "Anne" : 
                                 updatedStudent?.payment_responsible === "baba" ? "Baba" : 
                                 "Belirtilmemiş"}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-start">
                          <FaIdCard className="mt-1 mr-2 text-gray-500" />
                          <div className="w-full">
                            <p className="text-sm text-gray-500">Sorumlu TC Kimlik No</p>
                            {isEditing ? (
                              <input
                                type="text"
                                name="responsible_tc_number"
                                value={editFormData.responsible_tc_number || ""}
                                onChange={handleFormChange}
                                className="w-full p-2 border rounded-md"
                                placeholder="TC Kimlik Numarası"
                                maxLength="11"
                              />
                            ) : (
                              <p className="font-medium">
                                {updatedStudent?.responsible_tc_number || "Belirtilmemiş"}
                              </p>
                            )}
                          </div>
                        </div>
                        {/* Ödeme Sorumlusu Telefon ve E-posta Bilgileri */}
                        {updatedStudent?.payment_responsible && (
                          <>
                            <div className="flex items-start">
                              <FaPhone className="mt-1 mr-2 text-gray-500" />
                              <div className="w-full">
                                <p className="text-sm text-gray-500">Sorumlu Telefon</p>
                                <p className="font-medium">
                                  {updatedStudent?.payment_responsible === "mother" 
                                    ? (updatedStudent?.mother_phone || "Belirtilmemiş")
                                    : updatedStudent?.payment_responsible === "father" 
                                    ? (updatedStudent?.father_phone || "Belirtilmemiş")
                                    : updatedStudent?.payment_responsible === "anne" 
                                    ? (updatedStudent?.mother_phone || "Belirtilmemiş")
                                    : updatedStudent?.payment_responsible === "baba" 
                                    ? (updatedStudent?.father_phone || "Belirtilmemiş")
                                    : "Belirtilmemiş"}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start">
                              <FaEnvelope className="mt-1 mr-2 text-gray-500" />
                              <div className="w-full">
                                <p className="text-sm text-gray-500">Sorumlu E-posta</p>
                                <p className="font-medium">
                                  {updatedStudent?.payment_responsible === "mother" 
                                    ? (updatedStudent?.mother_email || "Belirtilmemiş")
                                    : updatedStudent?.payment_responsible === "father" 
                                    ? (updatedStudent?.father_email || "Belirtilmemiş")
                                    : updatedStudent?.payment_responsible === "anne" 
                                    ? (updatedStudent?.mother_email || "Belirtilmemiş")
                                    : updatedStudent?.payment_responsible === "baba" 
                                    ? (updatedStudent?.father_email || "Belirtilmemiş")
                                    : "Belirtilmemiş"}
                                </p>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sağ Kolon */}
              <div>
                {/* Ödeme Durumu Kartı */}
                <div className="mb-4 sm:mb-6 bg-gray-50 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                    <FaMoneyBillWave className="mr-1 sm:mr-2 text-blue-600 text-sm sm:text-base" />
                    Ödeme Durumu
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-500">Toplam Tutar</p>
                      <p className="text-xl font-bold text-gray-800">
                        {totalAmount} ₺
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-500">Kalan Tutar</p>
                      <p className="text-xl font-bold text-red-600">
                        {totalUnpaidAmount} ₺
                      </p>
                    </div>
                  </div>

                  {/* İlerleme Çubuğu */}
                  <div className="mt-4">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        Ödeme Tamamlanma
                      </span>
                      <span className="text-sm font-medium text-gray-700">
                        {paymentPercentage}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-green-600 h-2.5 rounded-full"
                        style={{ width: `${paymentPercentage}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-gray-500">
                      <span>Ödenen: {totalPaidAmount} ₺</span>
                      <span>
                        Toplam Taksit:{" "}
                        {parsedPaymentPlan?.installmentCount || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Ödeme Planı Tabloları */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm">
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                      <FaMoneyBillWave className="mr-1 sm:mr-2 text-blue-600 text-sm sm:text-base" />
                      Bekleyen Ödemeler
                    </h3>

                    {parsedPaymentPlan?.unpaidBills &&
                    parsedPaymentPlan.unpaidBills.length > 0 ? (
                      <>
                        {/* Mobil Görünüm */}
                        <div className="block lg:hidden space-y-3">
                          {parsedPaymentPlan.unpaidBills.map((bill, index) => (
                            <div
                              key={index}
                              className="bg-white p-4 rounded-lg border border-gray-200"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <span className="font-medium text-gray-900">
                                  {bill.installmentNumber}. Taksit
                                </span>
                                <span
                                  className={`px-2 py-1 text-xs rounded-full ${
                                    bill.status === "overdue"
                                      ? "bg-red-100 text-red-800"
                                      : "bg-yellow-100 text-yellow-800"
                                  }`}
                                >
                                  {bill.status === "overdue"
                                    ? "Gecikti"
                                    : "Beklemede"}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 space-y-1 mb-3">
                                <div>Son Ödeme: {formatDateDisplay(bill.dueDate)}</div>
                                <div>Orijinal Tutar: {formatCurrency(bill.originalAmount || bill.amount)} ₺</div>
                                {bill.paidAmount > 0 && (
                                  <div className="text-green-600">Ödenen: {formatCurrency(bill.paidAmount)} ₺</div>
                                )}
                                <div className="font-medium">
                                  Kalan: {formatCurrency(bill.remainingAmount || bill.amount)} ₺
                                </div>
                              </div>
                              <button
                                onClick={() =>
                                  markInstallmentAsPaid(bill.installmentId)
                                }
                                disabled={paymentLoading}
                                className={`w-full px-3 py-2 text-white text-sm rounded-md transition-colors flex items-center justify-center ${
                                  paymentLoading
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-green-600 hover:bg-green-700"
                                }`}
                              >
                                {paymentLoading ? (
                                  <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    İşlem...
                                  </>
                                ) : (
                                  "Ödeme Al"
                                )}
                              </button>
                            </div>
                          ))}
                        </div>

                        {/* Desktop Görünüm */}
                        <div className="hidden lg:block overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 rounded-lg overflow-hidden">
                            <thead className="bg-gray-100">
                              <tr>
                                <th
                                  scope="col"
                                  className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Taksit No
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Son Ödeme Tarihi
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Orijinal / Ödenen / Kalan
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Durum
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {parsedPaymentPlan.unpaidBills
                                .slice()
                                .sort(
                                  (a, b) =>
                                    new Date(a.dueDate) - new Date(b.dueDate)
                                )
                                .map((installment, index) => {
                                  const installmentDate = new Date(
                                    installment.dueDate
                                  );
                                  const currentDate = new Date();
                                  const isPastDue =
                                    installmentDate < currentDate;

                                  return (
                                    <tr
                                      key={installment.id || index}
                                      className={isPastDue ? "bg-red-50" : ""}
                                    >
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        {installment.id || index + 1}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        {formatDateDisplay(installment.dueDate)}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="text-sm">
                                          
                                          {installment.paidAmount > 0 && (
                                            <div className="text-green-600">
                                              Ödenen: {formatCurrency(installment.paidAmount)} ₺
                                            </div>
                                          )}
                                          <div className="font-medium text-blue-600">
                                            Kalan: {formatCurrency(installment.remainingAmount || installment.amount)} ₺
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <span
                                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            isPastDue
                                              ? "bg-red-100 text-red-800"
                                              : "bg-yellow-100 text-yellow-800"
                                          }`}
                                        >
                                          {isPastDue ? "Gecikmiş" : "Bekliyor"}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-6 bg-white rounded-lg border border-gray-200">
                        <p className="text-gray-500">
                          Bekleyen taksit bulunmamaktadır.
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                      <FaMoneyBillWave className="mr-1 sm:mr-2 text-green-600 text-sm sm:text-base" />
                      Ödenen Taksitler
                    </h3>

                    {parsedPaymentPlan?.paidInstallments &&
                    parsedPaymentPlan.paidInstallments.length > 0 ? (
                      <>
                        {/* Mobil Görünüm */}
                        <div className="block lg:hidden space-y-3">
                          {parsedPaymentPlan.paidInstallments
                            .slice()
                            .sort(
                              (a, b) =>
                                new Date(a.paymentDate || a.dueDate) -
                                new Date(b.paymentDate || b.dueDate)
                            )
                            .map((installment, index) => (
                              <div
                                key={`${installment.id}-${index}`}
                                className="bg-white p-4 rounded-lg border border-gray-200"
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <span className="font-medium text-gray-900">
                                      {installment.partialPayment 
                                        ? `${installment.originalInstallmentId}. Taksit (Kısmi)`
                                        : `${installment.id}. Taksit`
                                      }
                                    </span>
                                    {installment.partialPayment && (
                                      <div className="text-xs text-blue-600 mt-1">
                                        Kısmi Ödeme
                                      </div>
                                    )}
                                  </div>
                                  <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                    Ödendi
                                  </span>
                                </div>
                                <div className="text-sm text-gray-600 space-y-1">
                                  <div>
                                    Vade Tarihi:{" "}
                                    {formatDateDisplay(installment.dueDate)}
                                  </div>
                                  <div>
                                    Ödeme Tarihi:{" "}
                                    {formatDateDisplay(installment.paymentDate)}
                                  </div>
                                  <div>Tutar: {formatCurrency(installment.amount)} ₺</div>
                                  <div className="flex gap-2 mt-2">
                                    <button
                                      onClick={() => handlePrintReceipt(installment)}
                                      className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm"
                                    >
                                      <FaFileDownload className="mr-1" /> Makbuz Al
                                    </button>
                                    {userRole === "admin" && (
                                      <button
                                        onClick={() => handleDeletePayment(installment)}
                                        className="inline-flex items-center text-red-600 hover:text-red-800 text-sm"
                                      >
                                        <FaTimes className="mr-1" /> Sil
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>

                        {/* Desktop Görünüm */}
                        <div className="hidden lg:block overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 rounded-lg overflow-hidden">
                            <thead className="bg-gray-100">
                              <tr>
                                <th
                                  scope="col"
                                  className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Taksit No
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Vade Tarihi
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Ödeme Tarihi
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Tutar
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  İşlem
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {parsedPaymentPlan.paidInstallments
                                .slice()
                                .sort(
                                  (a, b) =>
                                    new Date(a.paymentDate || a.dueDate) -
                                    new Date(b.paymentDate || b.dueDate)
                                )
                                .map((installment, index) => (
                                  <tr key={`${installment.id}-${index}`}>
                                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                                      <div>
                                        {installment.partialPayment 
                                          ? `${installment.originalInstallmentId} (Kısmi)`
                                          : installment.id
                                        }
                                      </div>
                                      {installment.partialPayment && (
                                        <div className="text-xs text-blue-600">
                                          Kısmi Ödeme
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                                      {formatDateDisplay(installment.dueDate)}
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                                      {formatDateDisplay(installment.paymentDate)}
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap font-medium">
                                      {formatCurrency(installment.amount)} ₺
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => handlePrintReceipt(installment)}
                                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                                        >
                                          <FaFileDownload className="mr-1" /> Makbuz Al
                                        </button>
                                        {userRole === "admin" && (
                                          <button
                                            onClick={() => handleDeletePayment(installment)}
                                            className="text-red-600 hover:text-red-800 text-sm flex items-center"
                                          >
                                            <FaTimes className="mr-1" /> Sil
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-6 bg-white rounded-lg border border-gray-200">
                        <p className="text-gray-500">
                          Ödenen taksit bulunmamaktadır.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Öğrenci Notları Bölümü */}
        <div className="mt-6 bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-green-800 p-4 text-white">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold flex items-center">
                <FaStickyNote className="mr-2" />
                Öğrenci Notları
              </h3>
              <button
                onClick={() => setIsNoteModalOpen(true)}
                className="px-4 py-2 bg-white text-green-600 rounded-md hover:bg-gray-100 transition-colors flex items-center text-sm font-medium"
              >
                <FaPlus className="mr-2" />
                Yeni Not Ekle
              </button>
            </div>
          </div>

          <div className="p-4">
            {notesLoading ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-2 text-gray-500">Notlar yükleniyor...</p>
              </div>
            ) : studentNotes.length > 0 ? (
              <div className="space-y-4">
                {studentNotes.map((note) => (
                  <div key={note.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                          <span className="font-medium">{note.created_by_username || 'Bilinmeyen'}</span>
                          <span>•</span>
                          <span>{new Date(note.created_at).toLocaleString('tr-TR')}</span>
                          {note.updated_at && note.updated_at !== note.created_at && (
                            <>
                              <span>•</span>
                              <span className="text-blue-600">Düzenlendi: {new Date(note.updated_at).toLocaleString('tr-TR')}</span>
                            </>
                          )}
                        </div>
                        {editingNote === note.id ? (
                          <div className="space-y-2">
                            <textarea
                              value={editingNoteText}
                              onChange={(e) => setEditingNoteText(e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              rows="3"
                              placeholder="Not içeriği..."
                              maxLength="5000"
                              disabled={isUpdatingNote}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  updateStudentNote(note.id, editingNoteText);
                                }}
                                disabled={isUpdatingNote || !editingNoteText.trim()}
                                className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm flex items-center"
                              >
                                <FaSave className="mr-1" />
                                {isUpdatingNote ? "Kaydediliyor..." : "Kaydet"}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingNote(null);
                                  setEditingNoteText("");
                                  setNewNote("");
                                }}
                                disabled={isUpdatingNote}
                                className="px-3 py-1 bg-gray-400 text-white rounded-md hover:bg-gray-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm"
                              >
                                İptal
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-gray-800 whitespace-pre-wrap">{note.note_text}</p>
                        )}
                      </div>
                      {!editingNote && (
                        <div className="flex gap-2 ml-4">
                          {userRole === "admin" && (
                            <button
                              onClick={() => {
                                if (editingNote) return; // Başka bir not düzenleniyorsa engelle
                                setEditingNote(note.id);
                                setEditingNoteText(note.note_text);
                                setNewNote(""); // newNote'u temizle
                              }}
                              disabled={editingNote !== null || isUpdatingNote}
                              className="p-1 text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                              title="Düzenle"
                            >
                              <FaEdit className="w-4 h-4" />
                            </button>
                          )}
                          {userRole === "admin" && (
                            <button
                              onClick={() => deleteStudentNote(note.id)}
                              disabled={editingNote !== null || isUpdatingNote}
                              className="p-1 text-red-600 hover:text-red-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                              title="Sil"
                            >
                              <FaTrash className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FaStickyNote className="mx-auto text-4xl text-gray-400 mb-4" />
                <p className="text-gray-500 mb-4">Henüz not eklenmemiş</p>
                <button
                  onClick={() => setIsNoteModalOpen(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  İlk Notu Ekle
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Not Ekleme Modalı */}
        {isNoteModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <FaStickyNote className="mr-2 text-green-600" />
                Yeni Not Ekle
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Not İçeriği
                  </label>
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    rows="6"
                    placeholder="Öğrenci ile ilgili notunuzu buraya yazın..."
                    maxLength="5000"
                  />
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {newNote.length}/5000 karakter
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setIsNoteModalOpen(false);
                    setNewNote("");
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={addStudentNote}
                  disabled={!newNote.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                >
                  <FaSave className="mr-2" />
                  Notu Kaydet
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Silme Onayı Modal */}
      {deleteConfirmOpen && userRole === "admin" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">
              Öğrenciyi Sil
            </h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
              <span className="font-semibold">
                {name} {surname}
              </span>{" "}
              adlı öğrenciyi silmek istediğinizden emin misiniz? Bu işlem geri
              alınamaz.
            </p>
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-2">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors order-2 sm:order-1"
              >
                İptal
              </button>
              <button
                onClick={handleDeleteStudent}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base bg-red-600 text-white rounded hover:bg-red-700 transition-colors order-1 sm:order-2"
              >
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ödeme Modalı */}
      {isModalOpen && (
        <PaymentModal
          student={{
            ...updatedStudent,
            // Eğer string ise ve ayrıştırılmış hali varsa onu kullan
            payment_plan: parsedPaymentPlan,
          }}
          closeModal={closeModal}
          onUpdate={handleUpdateStudent}
        />
      )}

      {/* Sözleşme Düzenleme Modalı */}
      {isContractModalOpen && (
        <ContractModal
          isOpen={isContractModalOpen}
          onClose={closeContractEdit}
          onSave={handleSaveContractEdit}
        />
      )}

      {/* Makbuz Modalı */}
      {isMakbuzModalOpen && (
        <MakbuzModal
          makbuzData={makbuzData}
          closeModal={() => setIsMakbuzModalOpen(false)}
        />
      )}
    </div>
  );
};

export default StudentDetails;
